// R123 (Jesse: "I want to see what issues have come in that need triage and
// assignments, then what's been triaged but isn't routed to a person /
// scheduled, then where all the issues scheduled for a particular date are"):
// the Issues board's client side of the derived pipeline stage. The ladder
// itself lives in lib/issueStage.mjs (shared with GET /issues/board, which
// stamps stage / lane / stage_why / stage_date / stage_flags on every row in
// all-sources mode); this module re-exports it with the board's row / summary
// types, the storage keys, and the row action model.
//
// CONTRACT (R123 §8, the contract commit): the exports below are what the
// board (builder B), the popover / sheets / Today card (builder C) and the
// server (builder A) code against. Rename nothing; add freely.
//   rowActions(r, viewer)  one blue primary, at most one secondary (desktop),
//                          then the ⋯ menu in the fixed §3 order
//   factLine(r, today)     the one line under the stage pill (escaped HTML)
//   stagePill(r, today)    the pill's words + tone
//   parentActions(kids, …) a parent row's → Assign N / Resolve all / ⋯
// The row helpers below (canAssignRow, canRestoreRow, routeGoneOf …) are the
// eligibility rules the board's buttons, bulk bar and route modal share.
import {
  STAGES, TERMINAL_STAGES, LANES, LIVE_LANES, LANE_OF, LANE_LABEL, STAGE_LABEL, WHY_LABEL, FLAGS, FLAG_LABEL, TURN_DONE, ROUTE_KINDS,
  bizDay, bizDayOf, ageBandOf, isLiveStage, dispatchWoIdOf, routedOf, stageOf, tallyStages,
  type Stage, type Lane, type LiveLane, type StageWhy, type StageFlag, type RouteKind, type Route, type StageResult, type StageFacts, type AgeBand,
} from '../../../../lib/issueStage.mjs';
import { diffDays } from '../../../../lib/woCalendar.mjs';
import type { WoDispatch } from '../../../../lib/woCalendar.mjs';
import { canOpenRecovery } from '../../../../lib/guestRecovery.mjs';
import type { KebabItem } from '../../shared/components/Kebab';
import { Formatters } from '../../shared/lib/formatters';
import { hasOpenWo, woIdOf } from './woSync';

export {
  STAGES, TERMINAL_STAGES, LANES, LIVE_LANES, LANE_OF, LANE_LABEL, STAGE_LABEL, WHY_LABEL, FLAGS, FLAG_LABEL, TURN_DONE, ROUTE_KINDS,
  bizDay, bizDayOf, ageBandOf, isLiveStage, dispatchWoIdOf, routedOf, stageOf, tallyStages,
};
export type { Stage, Lane, LiveLane, StageWhy, StageFlag, RouteKind, Route, StageResult, StageFacts, AgeBand, WoDispatch };

// ── payload shapes (R123 §6a) ────────────────────────────────────────────────
/** The R123 fields a board row carries. All optional: an older server sends none (the lane bar hides). */
export interface StagedRowFields {
  stage?: Stage;
  lane?: Lane;
  stage_why?: StageWhy;
  stage_date?: string | null;
  stage_flags?: StageFlag[];
  /** Inside the day chips' window (scope=live also returns older live rows; the All tab filters on this). */
  in_window?: boolean;
  /** The live WO's slim projection (lib/woCalendar woDispatch) — never money. */
  dispatch?: WoDispatch | null;
  /** An ao_issue_triage stamp. */
  triaged?: { at: string; by?: string } | null;
  /** The reporter's severity when a triage override changed it ("was High"). */
  severity_was?: string | null;
}
/** The R123 additions to GET /issues/board summary (legacy fields unchanged). */
export interface LaneSummary {
  by_stage?: Partial<Record<Stage, number>>;
  by_lane?: Partial<Record<Lane, number>>;
  lane_urgent?: Partial<Record<Lane, number>>;
  missed?: number;
  came_in?: { total: number; live: number };
  new_by_age?: { today: number; week: number; older: number };
  review_signals?: number;
  triage_unreadable?: boolean;
  stage_error?: boolean;
}
/** The R123 additions to a parent row (GET /issues/board parents[]). */
export interface StagedParentCounts { by_lane?: Partial<Record<Lane, number>> }
/** by_property additions. */
export interface StagedPropertyCounts { live_total?: number; to_triage?: number; to_schedule?: number }

/** The stage of a row the server already staged, or null (older server / single-source mode / stage_error). */
export function stageInfoOf(r: any): StageResult | null {
  if (!r || !STAGES.includes(r.stage)) return null;
  return {
    stage: r.stage,
    lane: (LANES as readonly string[]).includes(r.lane) ? r.lane : LANE_OF[r.stage as Stage],
    why: r.stage_why || (r.stage === 'done' ? 'resolved' : r.stage === 'dismissed' ? 'dismissed' : 'new'),
    date: typeof r.stage_date === 'string' ? r.stage_date : null,
    flags: Array.isArray(r.stage_flags) ? r.stage_flags.filter((f: any) => (FLAGS as readonly string[]).includes(f)) : [],
    who: null,
  };
}
/** True when the payload carries stages (the lane UI renders); false → today's board exactly. */
export function hasStages(rows: any[] | null | undefined): boolean {
  return Array.isArray(rows) && rows.some(r => r && STAGES.includes(r.stage));
}

// ── viewer + roles ───────────────────────────────────────────────────────────
export const OFFICE_ROLES: readonly string[] = Object.freeze(['admin', 'director', 'manager', 'remote_ops']);
export const isOfficeRole = (role: string | null | undefined): boolean => OFFICE_ROLES.includes(String(role || ''));
export interface Viewer {
  role: string;
  /** admin / director / manager / remote_ops: triage, assign, book, due, severity override (D9: client-enforced). */
  office: boolean;
  /** ≤ 720px: primary + ⋯ only, no secondary. */
  narrow: boolean;
  /**
   * R123 review #16: the triage store could be read (summary.triage_unreadable is not set). False hides
   * Triage / Mark triaged / Undo triage: every stamped row reads New then, and a write would fail too.
   * Absent = readable.
   */
  triage?: boolean;
}
export function currentViewer(): Viewer {
  let role = '';
  try { role = sessionStorage.getItem('userRole') || ''; } catch { /* private mode */ }
  let narrow = false;
  try { narrow = typeof window !== 'undefined' && window.innerWidth <= 720; } catch { /* no window */ }
  return { role, office: isOfficeRole(role), narrow };
}

// ── the action model (R123 §3) ───────────────────────────────────────────────
/** One inline button. data-act + attrs are what IssuesPage's bind() handlers look for. */
export interface RowAction {
  act: string;
  label: string;
  icon?: string;
  title?: string;
  attrs?: Record<string, string | number | boolean | null | undefined>;
  tone?: 'primary' | 'ghost' | 'danger';
}
export interface RowActions {
  /** One blue primary (null only on a closed row with nothing to undo / restore). */
  primary: RowAction | null;
  /** At most one, desktop only (never when viewer.narrow). */
  secondary?: RowAction | null;
  /** The ⋯ menu in the fixed §3 order, ineligible items left out. */
  menu: KebabItem[];
}

// ── row helpers (the eligibility rules every button / bulk action shares) ──
const escH = (s: any): string => Formatters.escapeHtml(s == null ? '' : String(s));
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
export const FIELD_ROLES: readonly string[] = Object.freeze(['cleaner', 'inspector', 'maintenance']);
export const isFieldRole = (role: string | null | undefined): boolean => FIELD_ROLES.includes(String(role || ''));
/** D12: a strike counts against a cleaner / inspector, so only these sources offer it. */
export const STRIKE_SOURCES: readonly string[] = Object.freeze(['housekeeping', 'inspection', 'logged']);
export const SEV_WORD: Readonly<Record<string, string>> = Object.freeze({ emergency: 'Emergency', high: 'High', med: 'Medium', medium: 'Medium', low: 'Low' });
const isLiveRow = (r: any): boolean => !!r && r.status !== 'resolved' && r.status !== 'dismissed';
const firstName = (v: any): string => String(v == null ? '' : v).trim().split(/[\s@]+/)[0] || '';

/** "Thu Oct 23" (weekday) or "Oct 23" for a bare YYYY-MM-DD; '' for anything else. Never shifted. */
export function dayLabel(d: string | null | undefined, weekday = true): string {
  const s = String(d || '').slice(0, 10);
  if (!DAY_RE.test(s)) return '';
  const x = new Date(`${s}T00:00:00Z`);
  if (isNaN(x.getTime())) return '';
  return x.toLocaleDateString('en-US', weekday ? { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' } : { month: 'short', day: 'numeric', timeZone: 'UTC' }).replace(',', '');
}
// The board formats a day per row per draw: ONE cached Pacific formatter (lib bizDayOf builds one per call).
let ptFmt: Intl.DateTimeFormat | null = null;
/** lib/issueStage bizDayOf with a cached formatter — same rules: a bare YYYY-MM-DD is as-is, "YYYY-MM-DD HH:mm:ss" is UTC. */
export function pacificDay(iso: any): string | null {
  const s = iso == null ? '' : String(iso);
  if (!s) return null;
  if (DAY_RE.test(s)) return s;
  const t = Date.parse(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s) ? `${s.replace(' ', 'T')}Z` : s);
  if (!Number.isFinite(t)) return null;
  try { ptFmt = ptFmt || new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit' }); return ptFmt.format(new Date(t)); }
  catch { return bizDayOf(s); }
}
let dayMemo = { at: 0, day: '' };
/** The Pacific business day, memoised for 30 s (rowActions runs per row per draw). */
export function todayPT(): string {
  const n = Date.now();
  if (!dayMemo.day || n - dayMemo.at > 30000) dayMemo = { at: n, day: bizDay() };
  return dayMemo.day;
}
/** A timestamp's Pacific day as "Oct 20" (a bare YYYY-MM-DD is shown as-is, never shifted). '' when unreadable. */
export function pacificDate(iso: any): string { return dayLabel(pacificDay(iso), false); }
/** Whole Pacific days from a timestamp to today (0 = today), or null. */
export function ageDays(iso: any, today: string): number | null {
  const d = pacificDay(iso);
  if (!d || !DAY_RE.test(String(today || ''))) return null;
  const n = diffDays(d, today);
  return n == null ? null : Math.max(0, n);
}
/** lib ageBandOf on the cached formatter: 'today' | 'week' (1–6 days) | 'older' (7+, or no date). */
export function ageBand(iso: any, today: string): AgeBand {
  const n = ageDays(iso, today);
  return n == null ? 'older' : n <= 0 ? 'today' : n < 7 ? 'week' : 'older';
}

/** The row's stage: the server's stamp, else the shared ladder run here (a row the server did not stage). */
export function stageFor(r: any, today: string): StageResult {
  return stageInfoOf(r) || stageOf(r, { today: DAY_RE.test(String(today || '')) ? today : todayPT() });
}
/** The row's work order was deleted in Maintenance. */
export function woGoneOf(r: any): boolean { return !!(r && r.wo && r.wo.chip === 'missing'); }
/** Its task was canceled / deleted, or its Guest Recovery case / clean is gone: the issue is free again. */
export function routeGoneOf(r: any): boolean {
  const rt = routedOf(r);
  if (!rt) return false;
  if (rt.kind === 'task') return rt.status === 'missing' || rt.status === 'canceled';
  return (rt.kind === 'recovery' || rt.kind === 'clean') && rt.status === 'missing';
}
/** → Assign is open: not a WO row, no live WO, no live route, no supply hand-off, not dismissed. */
export function canAssignRow(r: any): boolean {
  if (!r || r.source === 'maintenance' || r.status === 'dismissed') return false;
  if (r.wo_id && !woGoneOf(r)) return false;
  if (r.routed && !routeGoneOf(r)) return false;
  return !r.routed_supply;
}
/** Restore = undo what the board did (a dismiss, an in-place resolve, a board WO close). Office only (the server 403s field logins). */
export function canRestoreRow(r: any, viewer: Viewer): boolean {
  if (!r || isFieldRole(viewer.role)) return false;
  return r.status === 'dismissed' || (r.status === 'resolved' && r.overlay_state === 'resolved' && r.source !== 'maintenance') || !!r.wo_undo;
}
/** A routed chip links only where this viewer can land: a task for anyone, Guest Recovery for its roles. */
export function routedOpensFor(rt: { kind?: string } | null | undefined, viewer: Viewer): boolean {
  if (!rt) return false;
  if (rt.kind === 'task') return true;
  return rt.kind === 'recovery' && canOpenRecovery(viewer.role);
}
/** The office places an issue already has (a live work order, a task, Guest Recovery). */
export function destinationsOf(r: any): { wo: boolean; task: boolean; recovery: boolean } {
  if (!r) return { wo: false, task: false, recovery: false };
  const k = r.routed && !routeGoneOf(r) ? String(r.routed.kind || '') : '';
  return { wo: !!(r.wo_id || r.wo || r.source === 'maintenance') && !woGoneOf(r), task: k === 'task' || !!r.also_task, recovery: k === 'recovery' || !!r.also_recovery };
}
/** ＋ Also send: it already has a destination and there is an office place it does not have yet. */
export function canAlsoSend(r: any, viewer: Viewer): boolean {
  const dest = destinationsOf(r);
  return (dest.wo || dest.task || dest.recovery) && isLiveRow(r) && !r.system && (!dest.wo || !dest.task || (!dest.recovery && viewer.office));
}
/** The person on it: the WO's vendor / assignee, else the route's cleaner / task owner. */
export function whoOf(r: any): string | null {
  const d = r && r.dispatch;
  if (d && d.who) return String(d.who);
  const rt = routedOf(r);
  return rt && rt.who ? rt.who : null;
}
/** Days an untouched hand-off has waited: since the triage stamp / route / the WO was opened / the issue came in. */
export function waitDays(r: any, today: string): number | null {
  if (!r) return null;
  const d = r.dispatch;
  if (d && typeof d.age_days === 'number') return d.age_days;
  const rt = routedOf(r);
  const since = (r.triaged && r.triaged.at) || (rt && rt.at) || r.created_at;
  return ageDays(since, today);
}

/** review #12: the reporter's severity word when a triage override changed it ("Medium"), else ''. */
export function sevBackOf(r: any): string {
  if (!r || !r.severity_was || r.severity_was === r.severity) return '';
  return SEV_WORD[String(r.severity_was)] || String(r.severity_was);
}

// ── the stage pill ───────────────────────────────────────────────────────────
export type StageTone = 'new' | 'muted' | 'warn' | 'info' | 'bad' | 'good';
/** The pill's words (R123 §3 table) and tone. */
export function stagePill(r: any, today: string): { label: string; tone: StageTone; title: string } {
  const si = stageFor(r, today);
  const rt = routedOf(r);
  const title = WHY_LABEL[si.why] || STAGE_LABEL[si.stage] || '';
  switch (si.stage) {
    case 'new': return si.why === 'review_signal' ? { label: 'Review theme', tone: 'muted', title: 'A recurring theme in guest reviews' } : { label: 'New', tone: 'new', title: 'Came in, not triaged yet' };
    case 'triaged':
      if (si.why === 'route_gone') {
        const label = woGoneOf(r) ? 'WO deleted · re-assign'
          : rt && rt.kind === 'task' ? (rt.status === 'missing' ? 'Task deleted · re-assign' : 'Task canceled · re-assign')
          : rt && rt.kind === 'recovery' ? 'Case gone · re-assign'
          : rt && rt.kind === 'clean' ? 'Clean gone · re-assign' : 'Re-assign';
        return { label, tone: 'bad', title: 'Where it was sent is gone. Assign it again if it still needs doing.' };
      }
      return { label: 'Triaged', tone: 'muted', title: 'Looked at, not routed to anyone yet' };
    case 'no_owner': return { label: STAGE_LABEL.no_owner, tone: 'warn', title: 'A work order exists, but no person or vendor is on it' };
    case 'no_date': return si.why === 'followup' ? { label: 'Follow-up needed', tone: 'warn', title: 'The tech flagged more work; no day booked' } : { label: STAGE_LABEL.no_date, tone: 'warn', title: 'Someone is on it, no day booked' };
    case 'scheduled':
      if (si.why === 'missed') return { label: `Missed ${dayLabel(si.date, false)}`.trim(), tone: 'bad', title: 'The booked day passed and the work order is still open' };
      if (si.why === 'next_clean') return { label: si.date ? `Next clean ${dayLabel(si.date)}` : 'Next clean', tone: 'info', title: 'On the housekeeper\'s checklist for the next clean' };
      if (si.why === 'task_due') return { label: `Task due ${dayLabel(si.date, false)}`.trim(), tone: si.flags.includes('missed') ? 'bad' : 'info', title: si.flags.includes('missed') ? 'The task is past its due date' : 'On the Tasks board with a due date' };
      return { label: si.date ? `Booked ${dayLabel(si.date)}` : 'Booked', tone: 'info', title: 'A person and a day are booked' };
    case 'verify': return { label: "Check it's fixed", tone: 'good', title };
    case 'in_work': return { label: 'In progress', tone: 'warn', title };
    case 'handed_off': {
      const label = si.why === 'next_inspection' ? 'With next inspection' : si.why === 'recovery' ? 'With Guest Recovery' : si.why === 'task' ? 'Task (no due date)' : si.why === 'supply' ? 'Supply request' : 'With others';
      return { label, tone: 'muted', title };
    }
    case 'unknown': return { label: 'WO unreadable', tone: 'muted', title: "Couldn't read the work orders just now. Showing the last known state." };
    case 'done': return { label: 'Done', tone: 'good', title };
    default: return { label: 'Dismissed', tone: 'muted', title };
  }
}

// ── the fact line ────────────────────────────────────────────────────────────
const WO_WHYS = new Set(['vendor_done', 'all_ticked', 'timer', 'on_site', 'no_day', 'booked', 'was_booked', 'followup', 'missed']);
/**
 * The one line under the stage pill, as escaped HTML ("Bay Plumbing · Thu Oct 23 · due Oct 25 · 1/3 ·
 * waiting on owner"; "Next clean · Fri Oct 24 · Maria"; "Clean day passed Oct 20 · check it"). An overdue
 * due date and a missed booking read red (.iss-f-bad). '' on a closed row.
 */
export function factLine(r: any, today: string): string {
  if (!r) return '';
  const si = stageFor(r, today);
  if (si.stage === 'done' || si.stage === 'dismissed') return '';
  const bits: { t: string; tone?: 'bad' | 'info' }[] = [];
  const add = (t: string | null | undefined, tone?: 'bad' | 'info') => { if (t) bits.push({ t, tone }); };
  const d = r.dispatch && typeof r.dispatch === 'object' ? r.dispatch : null;
  const rt = routedOf(r);
  if (d && WO_WHYS.has(si.why)) {
    if (d.who) add(String(d.who));
    else if (si.stage !== 'no_owner') add('Nobody assigned');
    if (d.booked) {
      if (si.why === 'missed' || si.flags.includes('missed')) add(`missed ${dayLabel(d.booked, false)}`, 'bad');
      else if ((d.was_booked || si.why === 'was_booked') && si.stage !== 'verify' && si.stage !== 'in_work') add(`booked day passed ${dayLabel(d.booked, false)}`, 'bad');   // done / on site: the day is history, not a problem
      else add(d.booked_end && d.booked_end !== d.booked ? `${dayLabel(d.booked)} to ${dayLabel(d.booked_end)}` : dayLabel(d.booked));
    } else add('no day');
    if (d.due_source === 'checkout') add(d.due ? `due at checkout ${dayLabel(d.due, false)}` : 'due at next checkout', d.overdue ? 'bad' : undefined);
    else if (d.due) add(`due ${dayLabel(d.due, false)}`, d.overdue ? 'bad' : undefined);
    if (d.items_n) add(`${d.items_done || 0}/${d.items_n}`);
    if (d.owner_state === 'needs_approval') add('waiting on owner');
    if (d.quote_pending) add('quote pending');
    if (d.vendor_done) add('vendor says done');
    else if (d.all_ticked) add('every item ticked');
    if (d.timer_on) add('timer running');
    if (d.followup && si.why !== 'followup') add('follow-up needed');
  } else if (si.why === 'route_gone') {
    add(woGoneOf(r) ? 'Its work order was deleted'
      : rt && rt.kind === 'task' ? (rt.status === 'missing' ? 'Its task was deleted' : 'Its task was canceled')
      : rt && rt.kind === 'recovery' ? 'Its Guest Recovery case is gone'
      : rt && rt.kind === 'clean' ? 'Its clean is gone' : 'Where it went is gone');
    add('assign it again');
  } else if (rt) {
    if (rt.kind === 'clean') {
      if (si.why === 'clean_done') { add(`Clean done${rt.date ? ` ${dayLabel(rt.date, false)}` : ''}`); add('check it'); }
      else if (si.why === 'clean_passed') { add(`Clean day passed ${dayLabel(rt.date, false)}`.trim()); add('check it'); }
      else if (si.why === 'cleaning') { add('Cleaning now'); add(rt.who); }
      else { add('Next clean'); add(rt.date ? dayLabel(rt.date) : 'date not set'); add(rt.who); }
    } else if (rt.kind === 'task') {
      if (si.why === 'task_due') { add('Task'); add(`due ${dayLabel(rt.due_date, false)}`, si.flags.includes('missed') ? 'bad' : undefined); }
      else if (si.stage === 'in_work') add(rt.status === 'blocked' ? 'Task blocked' : 'Task in progress');
      else { add('Task'); add('no due date'); }
      add(rt.who);
    } else if (rt.kind === 'recovery') {
      add('Guest Recovery');
      add(rt.status && rt.status !== 'open' ? rt.status.replace(/_/g, ' ') : 'open case');
    } else if (rt.kind === 'supply') add('Supply request');
    else if (rt.kind === 'inspection') { add('Next inspection'); add('no date yet'); }
    if (rt.via === 'log') add('sent from Log Issues');
  } else if (si.stage === 'triaged') {
    const t = r.triaged && typeof r.triaged === 'object' ? r.triaged : null;
    add(t && t.at ? `Triaged ${pacificDate(t.at)}`.trim() : 'Triaged');
    add(t && t.by ? firstName(t.by) : '');
    add('not routed yet');
  } else if (si.stage === 'in_work') add('In progress, not sent anywhere yet');
  else if (si.stage === 'unknown') add("Couldn't read the work order just now");
  if (si.flags.includes('new_info')) add('new info', 'info');
  else if (si.flags.includes('asked')) add('asked for info');
  return bits.map(b => (b.tone ? `<span class="iss-f-${b.tone}">${escH(b.t)}</span>` : escH(b.t))).join(' · ');
}

// ── the action model ─────────────────────────────────────────────────────────
const item = (act: string, label: string, icon: string, o: { title?: string; danger?: boolean; attrs?: Record<string, any> } = {}): KebabItem =>
  ({ label, icon, title: o.title, danger: o.danger, attrs: { 'data-act': act, ...(o.attrs || {}) } });
const SEP: KebabItem = { divider: true };
/** Drop leading / trailing / doubled dividers. */
function tidy(menu: KebabItem[]): KebabItem[] {
  const out: KebabItem[] = [];
  for (const it of menu) { if (it.divider && (!out.length || out[out.length - 1].divider)) continue; out.push(it); }
  while (out.length && out[out.length - 1].divider) out.pop();
  return out;
}

/**
 * Primary / secondary / ⋯ per stage and why (R123 §3 tables). Every action keeps its data-act (and
 * data-wo / data-focus …) so IssuesPage's per-row handlers bind to it wherever Kebab moves it.
 *   primary   one blue button (null only on a closed row with nothing to undo / restore)
 *   secondary desktop only: Dismiss… (To triage), Resolve (To schedule / Scheduled / With others),
 *             Still broken (Check & close); none on closed rows
 *   menu      Details · → Assign · Also send · Work order / Maintenance / Calendar · Mark / Undo triage ·
 *             supply hint · reviews | Ask · Add photos | Resolve · Dismiss · Claim · Strike · Restore
 */
export function rowActions(r: any, viewer: Viewer): RowActions {
  if (!r) return { primary: null, secondary: null, menu: [] };
  const today = todayPT();
  const si = stageFor(r, today);
  const live = isLiveRow(r);
  const office = !!viewer.office;
  const canTriage = office && viewer.triage !== false;   // review #16: not while the triage store is unreadable
  const woId = woIdOf(r);
  const woLive = !!woId && !woGoneOf(r);
  const rt = routedOf(r);
  const rtLive = !!rt && !routeGoneOf(r);
  const assignable = canAssignRow(r);
  const restorable = canRestoreRow(r, viewer);
  const miss = r.cause === 'cleaning_miss';
  const assignWord = routeGoneOf(r) || woGoneOf(r) ? '↻ Re-assign' : miss ? '→ Cleaner' : '→ Assign';
  const assignTitle = 'Assign to maintenance, the next clean, the next inspection, a supply request, a task, or Guest Recovery';
  const woPop = (label: string, focus?: 'who' | 'book' | 'due', title?: string): RowAction => ({ act: 'wo-pop', label, title: title || 'Open the work order here (who, booked day, due date, checklist, comments)', attrs: { 'data-wo': woId, 'data-focus': focus } });
  const cleanOpen = (): RowAction => ({ act: 'clean-open', label: 'Open clean', title: 'Open this clean on the Calendar', attrs: { 'data-turn': rt && rt.ref_id, 'data-date': rt && rt.date, 'data-listing': r.listing_id } });
  const taskOpen = (): RowAction => ({ act: 'routed-open', label: 'Open task', title: 'Open the task on the Tasks board', attrs: { 'data-kind': 'task', 'data-ref': rt && rt.ref_id } });
  const caseOpen = (): RowAction => ({ act: 'routed-open', label: 'Open case', title: 'Open the Guest Recovery case', attrs: { 'data-kind': 'recovery', 'data-ref': rt && rt.ref_id } });
  const resolveA = (label = 'Resolve'): RowAction => ({ act: 'resolve', label, title: hasOpenWo(r) ? (r.source === 'maintenance' ? 'Close this work order (an optional note and done-on date)' : 'Resolve it on its work order (ticks its line, closes the WO when nothing else is open on it)') : 'Mark resolved, no further action needed' });
  const details = (): RowAction => ({ act: 'detail', label: 'Details', title: 'Open the source: the review, the guest-message chain, or the finding' });
  const restoreA = (): RowAction => ({ act: 'restore', label: r.wo_undo ? '↩ Undo' : 'Restore', title: r.wo_undo ? 'Undo: reopens the work order this closed' : 'Put it back on the board as open' });

  // ── primary ──
  let primary: RowAction | null = null;
  switch (si.stage) {
    case 'new':
      primary = miss && assignable ? { act: 'wo', label: '→ Cleaner', title: 'A staging miss: send it to the cleaner (next clean), or assign elsewhere' }
        : canTriage && r.source !== 'maintenance' ? { act: 'triage', label: 'Triage', title: 'Set the severity and decide where it goes (or decide later)' }
        : assignable ? { act: 'wo', label: '→ Assign', title: assignTitle }
        : details();
      break;
    case 'triaged':
      primary = assignable ? { act: 'wo', label: assignWord, title: si.why === 'route_gone' ? 'Where it went is gone. Assign it again if it still needs doing' : assignTitle } : details();
      break;
    case 'no_owner': primary = woLive ? (office ? woPop('Assign person', 'who', 'Put a person or vendor on the work order') : woPop('View WO')) : details(); break;
    case 'no_date': primary = woLive ? (office ? woPop('Book a day', 'book', 'Book the day the work happens') : woPop('View WO')) : details(); break;
    case 'scheduled':
      // review #10 / #24: every Calendar view is office-only, so a field login gets Details here (never a lock screen)
      if (si.why === 'next_clean') primary = rt && rt.ref_id && office ? cleanOpen() : details();
      else if (si.why === 'task_due') primary = rt && rt.ref_id ? taskOpen() : details();
      else if (!woLive) primary = details();
      else if (si.why === 'missed') primary = office ? woPop('Rebook', 'book', 'The booked day passed: book a new day') : woPop(`WO · ${dayLabel(si.date, false)}`.replace(/ · $/, ''));
      else primary = woPop(si.date ? `WO · ${dayLabel(si.date)}` : 'View WO');
      break;
    case 'verify': primary = live ? resolveA('Fixed') : details(); break;
    case 'in_work':
      if (si.why === 'cleaning') primary = rt && rt.ref_id && office ? cleanOpen() : details();
      else if (si.why === 'task') primary = rt && rt.ref_id ? taskOpen() : details();
      else if (si.why === 'record' || !woLive) primary = details();
      else primary = woPop('View WO');
      break;
    case 'handed_off':
      if (si.why === 'task' && rt && rt.ref_id) primary = taskOpen();
      else if (si.why === 'recovery' && rt && rt.ref_id && routedOpensFor(rt, viewer)) primary = caseOpen();
      else primary = live ? resolveA() : details();
      break;
    case 'unknown': primary = woId ? woPop('View WO') : details(); break;
    case 'done': case 'dismissed': primary = restorable ? restoreA() : null; break;
  }

  // ── secondary (desktop only) ──
  let second: RowAction | null = null;
  if (live) {
    if (si.lane === 'triage') second = { act: 'dismiss', label: 'Dismiss…', title: 'Not an issue' };
    // review #8 (D9): booking a day is the office's; a field login opens the WO to tell the team (comments)
    else if (si.lane === 'verify') second = woLive ? (office ? woPop('Still broken', 'book', 'Not fixed: book another day on the work order') : woPop('Still broken', undefined, 'Not fixed: open the work order and tell the team in a comment')) : { act: 'wo', label: 'Still broken', title: "Not fixed: send it somewhere else (e.g. a work order)" };
    else if (si.lane === 'schedule' || si.lane === 'scheduled' || si.lane === 'in_hand') second = primary && primary.act === 'resolve' ? null : resolveA();
  }
  const secondary = viewer.narrow ? null : second;
  const inline = new Set([primary && primary.act, secondary && secondary.act].filter(Boolean) as string[]);
  const plainWoPop = !!primary && primary.act === 'wo-pop' && !(primary.attrs && primary.attrs['data-focus']);

  // ── ⋯ (fixed order) ──
  const m: KebabItem[] = [];
  if (!inline.has('detail')) m.push(item('detail', 'Details', '≡', { title: 'Open the source and everything on it' }));
  if (assignable && !inline.has('wo')) m.push(item('wo', assignWord === '↻ Re-assign' ? 'Re-assign…' : miss ? 'Send to cleaner…' : 'Assign…', assignWord.charAt(0), { title: assignTitle }));
  if (canAlsoSend(r, viewer)) m.push(item('also', 'Also send…', '＋', { title: 'Send it somewhere else too (it stays where it is)' }));
  if (woLive) {
    if (!plainWoPop) m.push(item('wo-pop', 'Work order…', '🛠', { attrs: { 'data-wo': woId }, title: 'Open the work order here' }));
    m.push(item('wo-open', 'Open in Maintenance', '↗', { attrs: { 'data-wo': woId }, title: 'Open the work order on the Maintenance page' }));
    if (office) m.push(item('cal-open', 'Open in Calendar', '📅', { attrs: { 'data-wo': woId, 'data-listing': r.listing_id, 'data-date': (r.dispatch && r.dispatch.booked) || '' }, title: 'Open the work order on the Calendar' }));
  }
  if (rtLive && rt) {
    if (rt.kind === 'task' && rt.ref_id && !inline.has('routed-open')) m.push(item('routed-open', 'Open task', '✅', { attrs: { 'data-kind': 'task', 'data-ref': rt.ref_id } }));
    if (rt.kind === 'recovery' && rt.ref_id && routedOpensFor(rt, viewer) && !inline.has('routed-open')) m.push(item('routed-open', 'Open Guest Recovery case', '💛', { attrs: { 'data-kind': 'recovery', 'data-ref': rt.ref_id } }));
    if (rt.kind === 'clean' && rt.ref_id && office && !inline.has('clean-open')) m.push(item('clean-open', 'Open clean', '🧹', { attrs: { 'data-turn': rt.ref_id, 'data-date': rt.date, 'data-listing': r.listing_id } }));
  }
  if (canTriage && live && r.source !== 'maintenance') {
    if (si.stage === 'new') m.push(item('triage-mark', 'Mark triaged', '✓', { title: 'Looked at; decide where it goes later (it moves to To schedule)' }));
    // review #12: only while it still sits at "Triaged" on its stamp (a routed / scheduled row has moved on), and the
    // undo names the severity it puts back (the server drops the whole stamp, override included)
    else if (r.triaged && si.stage === 'triaged' && si.why === 'stamp') {
      const was = sevBackOf(r);
      m.push(item('triage-undo', was ? `Undo triage (severity back to ${was})` : 'Undo triage', '↩', { title: was ? `Back to To triage. The severity goes back to ${was}, as reported.` : 'Back to To triage' }));
    }
  }
  if (live && r.source === 'logged' && r.supply_hint && r.supply_hint.item && !r.supply_hint.order_id) m.push(item('supply-need', `Supply request: ${String(r.supply_hint.item).slice(0, 40)}?`, '📦', { title: 'Sounds like they ran out. Open a supply request (the issue itself stays open)' }));
  if (r.source === 'review') {
    const n = Array.isArray(r.review_ids) ? r.review_ids.length : 0;
    m.push(item('review-open', n > 1 ? `Open ${n} reviews` : 'Open review', '⭐', { attrs: { 'data-id': r.id }, title: 'Open the review(s) behind this on the Reviews page' }));
  }
  m.push(SEP);
  if (live && !r.system) m.push(item('ask', 'Ask for info', '❓', { title: 'Ask someone for more info (Slack, text, email or the app)' }));
  if (live) m.push(item('photo', `Add photos${r.action_photos ? ` (${r.action_photos})` : ''}`, '📷', { title: 'Attach photos or a video (pick several at once)' }));
  m.push(SEP);
  if (live) {
    if (second && !inline.has(second.act) && second.label === 'Still broken') m.push(item(second.act, 'Still broken', '↻', { attrs: second.attrs, title: second.title }));
    if (!inline.has('resolve')) m.push(item('resolve', 'Resolve', '✓', { title: resolveA().title }));
    if (!inline.has('dismiss')) m.push(item('dismiss', 'Dismiss…', '✕', { title: 'Not an issue' }));
    if (r.type === 'damage' && !r.claim_id && office) m.push(item('claim', 'Open a damage claim', '⚖', { title: 'Open a damage claim from this issue' }));
    if (STRIKE_SOURCES.includes(String(r.source))) m.push(item('strike', 'Flag strike', '⚠', { danger: true, title: 'Flag as a strike candidate (an admin reviews it before anything sticks)' }));
  }
  if (restorable && !inline.has('restore')) m.push(item('restore', r.wo_undo ? 'Undo' : 'Restore', '↩', { title: restoreA().title }));
  return { primary, secondary, menu: tidy(m) };
}

/** A parent row (an inspection / clean / guest thread / review with sub-issues): → Assign N · Resolve all · ⋯. */
export function parentActions(kids: any[], viewer: Viewer): RowActions & { counts: { assign: number; resolve: number; dismiss: number; restore: number; triage: number } } {
  const list = Array.isArray(kids) ? kids : [];
  const today = todayPT();
  const assign = list.filter(r => r.status === 'open' && canAssignRow(r)).length;
  const resolve = list.filter(r => isLiveRow(r) && (r.status === 'open' || hasOpenWo(r))).length;
  const dismiss = list.filter(r => isLiveRow(r)).length;
  const restore = list.filter(r => canRestoreRow(r, viewer)).length;
  const triage = viewer.office && viewer.triage !== false ? list.filter(r => isLiveRow(r) && r.source !== 'maintenance' && stageFor(r, today).stage === 'new').length : 0;
  const assignA: RowAction | null = assign ? { act: '', label: `→ Assign ${assign}`, title: `Assign the open sub-issues together: ONE work order with ${assign} items, or the next clean / inspection / one supply order`, attrs: { 'data-pact': 'assign' } } : null;
  const resolveA: RowAction | null = resolve ? { act: '', label: 'Resolve all', title: 'Mark every open sub-issue resolved', attrs: { 'data-pact': 'resolve' } } : null;
  const primary = assignA || resolveA;
  const secondary = !viewer.narrow && assignA ? resolveA : null;
  const pitem = (pact: string, label: string, icon: string, title: string): KebabItem => ({ label, icon, title, attrs: { 'data-pact': pact } });
  const menu: KebabItem[] = [];
  if (resolveA && primary !== resolveA && secondary !== resolveA) menu.push(pitem('resolve', 'Resolve all', '✓', 'Mark every open sub-issue resolved'));
  if (dismiss) menu.push(pitem('dismiss', 'Dismiss all', '✕', 'Dismiss every sub-issue shown'));
  if (restore) menu.push(pitem('restore', 'Restore all', '↩', 'Restore the sub-issues that were dismissed / resolved from this group'));
  if (triage) menu.push(pitem('triage', `Mark ${triage} triaged`, '✓', 'Looked at; decide where they go later'));
  return { primary, secondary, menu, counts: { assign, resolve, dismiss, restore, triage } };
}

// ── storage keys (R123 §8.8) ─────────────────────────────────────────────────
/** sessionStorage, one-shot: TodayPage writes the lane to land on, IssuesPage reads + clears it in render(). */
export const ISS_LANE_KEY = 'iss_lane';
/** localStorage: { lane, grouped: { [lane]: bool } } — the remembered lane + per-lane Grouped toggle. */
export const ISS_VIEW_KEY = 'iss_view_v1';
export type IssTab = LiveLane | 'all';
export interface IssView { lane?: IssTab; grouped?: Partial<Record<LiveLane, boolean>> }
const isTab = (v: any): v is IssTab => v === 'all' || (LIVE_LANES as readonly string[]).includes(v);

export function setIssLane(lane: IssTab): void {
  try { sessionStorage.setItem(ISS_LANE_KEY, lane); } catch { /* private mode */ }
}
/** Reads and clears the one-shot lane (null when absent or unknown). */
export function takeIssLane(): IssTab | null {
  let v: string | null = null;
  try { v = sessionStorage.getItem(ISS_LANE_KEY); sessionStorage.removeItem(ISS_LANE_KEY); } catch { /* private mode */ }
  return isTab(v) ? v : null;
}
export function readIssView(): IssView {
  try {
    const j = JSON.parse(localStorage.getItem(ISS_VIEW_KEY) || '{}');
    if (!j || typeof j !== 'object') return {};
    const out: IssView = {};
    if (isTab(j.lane)) out.lane = j.lane;
    if (j.grouped && typeof j.grouped === 'object') {
      const g: Partial<Record<LiveLane, boolean>> = {};
      for (const l of LIVE_LANES) if (typeof j.grouped[l] === 'boolean') g[l] = j.grouped[l];
      out.grouped = g;
    }
    return out;
  } catch { return {}; }
}
export function writeIssView(v: IssView): void {
  try { localStorage.setItem(ISS_VIEW_KEY, JSON.stringify(v || {})); } catch { /* private mode / quota */ }
}
